// Single source of truth for the two performance empty states shown on
// "The Record" card and the Performance tab.
//
//   State A — no trades yet          (hasTraded = false)
//   State B — trade placed, building  (hasTraded = true, chart not ready yet)
//
// x private (isSelf) / public views. Copy is the client's exact wording.
// Sitewide rule: NO punctuation in titles; punctuation IS used in body text.
import React from "react";

export const performanceEmptyCopy = ({ surface, building, isSelf }) => {
  if (surface === "record") {
    if (building) {
      return isSelf
        ? {
            title: "Your curve is building",
            body: "Check back after the next record update.",
          }
        : {
            // Desktop (lg+): drop "trader's" so the green line fits on ONE line
            // in the narrow Record card column ("building" was wrapping). Mobile
            // keeps the full wording (it's fine there).
            title: (
              <>
                This{" "}
                <span className="lg:hidden">trader's </span>
                curve is building
              </>
            ),
            body: "Check back after the next record update.",
          };
    }
    // No trades yet — two lines, first green (title) + second grey (body),
    // matching the building state's two-tone look. Same words, split across the
    // two colored lines.
    return isSelf
      ? { title: "Make your first trade", body: "to start your curve" }
      : {
          // Desktop (lg+): drop "trader's" so the title fits on ONE line in the
          // narrow Record card column ("This trader's curve starts" was wrapping,
          // same reason as the building state above). Mobile keeps the full wording.
          title: (
            <>
              This{" "}
              <span className="lg:hidden">trader's </span>
              curve starts
            </>
          ),
          body: "after their first trade",
        };
  }

  // surface === "performance"
  if (building) {
    return isSelf
      ? {
          title: "Your curve is building",
          body: "Check back after the next record update.",
          cta: null,
        }
      : {
          // Desktop (lg+): add "trader's" — the wide chart has room, and it keeps
          // this line a little different from the Record card. Mobile keeps the
          // shorter wording (no change there).
          title: (
            <>
              This{" "}
              <span className="hidden lg:inline">trader's </span>
              curve is building
            </>
          ),
          body: "Performance data will appear after the next record update.",
          cta: null,
        };
  }
  return isSelf
    ? {
        title: "Make your first trade to start your curve",
        body: "Your performance data appears after your first record update.",
        cta: "Place a Trade",
      }
    : {
        title: "No performance data yet",
        body: "This trader's curve starts with their first trade.",
        cta: null,
      };
};

// Decorative empty-state curves. Two DISTINCT shapes so the states read
// differently: a shorter/simpler "start" curve (no trades yet) and a more
// developed "building" curve (trade placed, waiting for the record). Rendered
// with Recharts (see the two surfaces) so the start dot is a true circle and the
// green gradient fill sits under the line.
export const EMPTY_CURVE_START = [
  { x: 0, v: 0 },
  { x: 1, v: 1.5 },
  { x: 2, v: 4 },
  { x: 3, v: 3 },
  { x: 4, v: 6 },
  { x: 5, v: 8.5 },
];
export const EMPTY_CURVE_BUILDING = [
  { x: 0, v: 0 },
  { x: 1, v: 3 },
  { x: 2, v: 7 },
  { x: 3, v: 5.5 },
  { x: 4, v: 9.5 },
  { x: 5, v: 12 },
  { x: 6, v: 10.5 },
  { x: 7, v: 14 },
  { x: 8, v: 16.5 },
];
export const emptyCurveData = (building) =>
  building ? EMPTY_CURVE_BUILDING : EMPTY_CURVE_START;

// Recharts dot renderer — draws a single filled circle at the START of the line
// only (index 0). Drawn in screen space, so it's a true round dot.
export const StartDot = (props) => {
  const { cx, cy, index } = props;
  if (index !== 0 || cx == null || cy == null) return null;
  return (
    <circle cx={cx} cy={cy} r={6} fill="#35b23c" stroke="#ffffff" strokeWidth={2} />
  );
};
